/** `x-widget: "resources"` — edits an object whose keys are arbitrary product /
 *  resource identifiers (the literal `*` is a valid catch-all key) and whose
 *  values are EITHER a tag-category code (string) OR a per-role object
 *  `{ resource, guide, driver }` (each a tag-category code or null).
 *
 * The grid cell shows a compact summary and an "Edit resources…" button that
 * opens a `<Dialog maxWidth="md">`. Each row has a key field and a mode select
 * ("Tag category" | "Roles"); switching the mode converts the value in place,
 * carrying a string code into `resource` when going string → roles.
 */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useEffect, useRef, useState } from 'react';

import { TagCategoryAutocomplete } from './TagCategoryAutocomplete';

// ---------------------------------------------------------------------------
// Value model
// ---------------------------------------------------------------------------

export interface RoleMap {
  resource: string | null;
  guide: string | null;
  driver: string | null;
}

/** Stored value type: keyed by resource id → code string | role object. */
export type ResourcesValue = Record<string, string | RoleMap>;

type Mode = 'string' | 'roles';
type Row = { key: string; mode: Mode; code: string | null; roles: RoleMap };

function isRoleMap(v: unknown): v is Partial<RoleMap> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function asResourcesValue(value: unknown): ResourcesValue {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: ResourcesValue = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (typeof v === 'string') out[k] = v;
    else if (isRoleMap(v)) {
      out[k] = {
        resource: typeof v.resource === 'string' ? v.resource : null,
        guide: typeof v.guide === 'string' ? v.guide : null,
        driver: typeof v.driver === 'string' ? v.driver : null,
      };
    }
  }
  return out;
}

function toRows(value: ResourcesValue): Row[] {
  return Object.entries(value).map(([key, v]) => {
    if (typeof v === 'string') {
      return { key, mode: 'string', code: v || null, roles: emptyRoles() };
    }
    return { key, mode: 'roles', code: null, roles: v };
  });
}

function emptyRoles(): RoleMap {
  return { resource: null, guide: null, driver: null };
}

/** Collapse rows back to the stored object (last non-empty key wins). */
function toValue(rows: Row[]): ResourcesValue {
  const out: ResourcesValue = {};
  for (const row of rows) {
    const k = row.key.trim();
    if (!k) continue;
    if (row.mode === 'string') {
      out[k] = row.code ?? '';
    } else {
      out[k] = {
        resource: row.roles.resource,
        guide: row.roles.guide,
        driver: row.roles.driver,
      };
    }
  }
  return out;
}

function summarize(value: ResourcesValue): string {
  const keys = Object.keys(value);
  if (keys.length === 0) return 'No resource mappings';
  const preview = keys.slice(0, 3).join(', ');
  return keys.length > 3 ? `${keys.length} resources: ${preview}…` : `${preview}`;
}

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

interface DialogProps {
  open: boolean;
  toId: number;
  value: ResourcesValue;
  readOnly: boolean;
  onClose: () => void;
  onDone: (next: ResourcesValue) => void;
}

function ResourcesDialog({ open, toId, value, readOnly, onClose, onDone }: DialogProps) {
  const [rows, setRows] = useState<Row[]>(() => toRows(value));

  // Re-seed whenever the dialog (re)opens for a possibly-different value.
  useEffect(() => {
    if (open) setRows(toRows(value));
  }, [open, value]);

  function patchRow(index: number, patch: Partial<Row>) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function setMode(index: number, mode: Mode) {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== index || r.mode === mode) return r;
        if (mode === 'roles') {
          // Carry the string code into `resource`.
          return { ...r, mode, roles: { ...emptyRoles(), resource: r.code }, code: null };
        }
        // roles → string: keep `resource` as the single code.
        return { ...r, mode, code: r.roles.resource, roles: emptyRoles() };
      }),
    );
  }

  function addRow() {
    setRows((prev) => [...prev, { key: '', mode: 'string', code: null, roles: emptyRoles() }]);
  }
  function removeRow(index: number) {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Resource → tag-category mappings</DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Map each product/resource id to a tag category, or split it by role. Use{' '}
          <Box component="code" sx={{ fontFamily: 'monospace' }}>
            *
          </Box>{' '}
          as a catch-all key.
        </Typography>

        <Stack spacing={1.5}>
          {rows.length === 0 && (
            <Typography variant="body2" color="text.disabled">
              No resource mappings.
            </Typography>
          )}
          {rows.map((row, index) => (
            <Box key={index} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <TextField
                    label="Resource id"
                    size="small"
                    fullWidth
                    placeholder="* for catch-all"
                    value={row.key}
                    disabled={readOnly}
                    onChange={(e) => patchRow(index, { key: e.target.value })}
                    sx={{ '& input': { fontFamily: 'monospace' } }}
                  />
                </Grid>
                <Grid item xs={8} sm={5}>
                  <TextField
                    select
                    label="Mode"
                    size="small"
                    fullWidth
                    value={row.mode}
                    disabled={readOnly}
                    onChange={(e) => setMode(index, e.target.value as Mode)}
                  >
                    <MenuItem value="string">Tag category</MenuItem>
                    <MenuItem value="roles">Roles</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={4} sm={2} sx={{ textAlign: 'right' }}>
                  {!readOnly && (
                    <Tooltip title="Remove mapping" placement="top" arrow>
                      <IconButton
                        size="small"
                        onClick={() => removeRow(index)}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Grid>

                {row.mode === 'string' ? (
                  <Grid item xs={12}>
                    <TagCategoryAutocomplete
                      toId={toId}
                      value={row.code}
                      onChange={(code) => patchRow(index, { code })}
                      readOnly={readOnly}
                      label="Tag category"
                    />
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={12} sm={4}>
                      <TagCategoryAutocomplete
                        toId={toId}
                        value={row.roles.resource}
                        onChange={(code) =>
                          patchRow(index, { roles: { ...row.roles, resource: code } })
                        }
                        readOnly={readOnly}
                        label="Resource"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TagCategoryAutocomplete
                        toId={toId}
                        value={row.roles.guide}
                        onChange={(code) =>
                          patchRow(index, { roles: { ...row.roles, guide: code } })
                        }
                        readOnly={readOnly}
                        label="Guide"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TagCategoryAutocomplete
                        toId={toId}
                        value={row.roles.driver}
                        onChange={(code) =>
                          patchRow(index, { roles: { ...row.roles, driver: code } })
                        }
                        readOnly={readOnly}
                        label="Driver"
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          ))}
        </Stack>

        {!readOnly && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addRow}
            sx={{ mt: 1.5, textTransform: 'none' }}
          >
            Add resource
          </Button>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onDone(toValue(rows))} disabled={readOnly}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Cell (rendered by the dispatcher)
// ---------------------------------------------------------------------------

interface CellProps {
  toId: number;
  value: unknown;
  onChange: (next: ResourcesValue) => void;
  readOnly?: boolean;
}

export function ResourcesCell({ toId, value, onChange, readOnly = false }: CellProps) {
  const [open, setOpen] = useState(false);
  const current = useRef<ResourcesValue>(asResourcesValue(value));
  current.current = asResourcesValue(value);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0, flex: 1 }}>
        {summarize(current.current)}
      </Typography>
      <Button
        size="small"
        onClick={() => setOpen(true)}
        sx={{ textTransform: 'none', flexShrink: 0 }}
      >
        {readOnly ? 'View resources…' : 'Edit resources…'}
      </Button>
      <ResourcesDialog
        open={open}
        toId={toId}
        value={current.current}
        readOnly={readOnly}
        onClose={() => setOpen(false)}
        onDone={(next) => {
          onChange(next);
          setOpen(false);
        }}
      />
    </Box>
  );
}
